import { useTranslation } from "react-i18next";
import { EnumProductType } from "gql/generated";
import {
  getMultilingualString,
  getResolvedProductSellingConfig,
} from "shared/utils";
import { getProductTypeConfig } from "shared/config/productTypeConfig";
import {
  BookerSection,
  resolveSectionOrder,
} from "shared/utils/widgetSectionOrder";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import {
  getEarlyRenewalDates,
  getTotalGuestCount,
} from "widgets/booker/shared/utils";
import { useOrgScopedUserOperators } from "widgets/booker/shared/hooks/useOrgScopedUserOperators";
import { useIsBookingRequest } from "widgets/booker/shared/hooks/useIsBookingRequest";
import { useIsInvoicedAfterService } from "widgets/booker/shared/hooks/useIsInvoicedAfterService";
import { isEventSelectionComplete } from "../Steps/components/EventFlow";
import { useDeliveryMethod } from "../Steps/components/DeliveryMethod";
import { useAddOnDetailsComplete } from "../Steps/components/AddOns";

export type CheckoutRequirementStatus =
  | "complete"
  | "incomplete"
  | "pending"
  | "error";

export type CheckoutRequirement = {
  key: string;
  label: string;
  status: CheckoutRequirementStatus;
  /** Steps to scroll to, in page order; the first incomplete one wins. */
  stepIds: string[];
  message?: string;
};

type Requirement = CheckoutRequirement & {
  /** Counts toward `isComplete`, whether or not the row is shown. */
  met: boolean;
  visible: boolean;
  section: BookerSection | "PAYMENT" | "POLICIES";
};

const SELECTION_STEP_IDS = [
  "merch-selection",
  "event-quantity",
  "event-session",
  "event-tier",
  "dates-and-guests",
  "purchase",
  "pricing-questions",
  "tier-selection",
];

/**
 * Everything that has to be done before the order can be submitted. The Pay
 * button and the checkout checklist both read from here so they can't
 * disagree.
 */
export const useCheckoutRequirements = () => {
  const {
    product,
    booker,
    userOperators,
    organizationId,
    paymentRequestGrandTotal,
    language,
    isCatalog,
  } = useBookerContext();
  const {
    grandTotal,
    depositTotal,
    cart,
    acceptedPolicies,
    customer,
    stripeCardElement,
    questionnaireComplete,
    selectedTierId,
    selectedOperatorId,
    paymentMethodId,
    eventGuestDetails,
    taxExempt,
    effectiveTaxExempt,
    renewableFeeQuote,
    customerEligibility,
    earlyRenewals,
  } = useCartContext();
  const isBookingRequest = useIsBookingRequest();
  const { isInvoicedAfterService, isMonthlyStatement } =
    useIsInvoicedAfterService();
  const isAddOnDetailsComplete = useAddOnDetailsComplete();
  const { isComplete: isDeliveryComplete, required: isDeliveryRequired } =
    useDeliveryMethod();
  const { t } = useTranslation("booker");
  const sellingConfig = getResolvedProductSellingConfig(
    product as typeof product & { sellingConfig?: any },
  );

  const isMerch = product?.type === EnumProductType.Merch;
  const hasMerchVariants =
    isMerch &&
    ((product as any)?.merchMetadata?.variants?.length ?? 0) > 0;
  const merchCartItems = cart.filter(
    item =>
      item.productType === EnumProductType.Merch && !!item.merchVariantId,
  );
  const isMerchComplete = hasMerchVariants
    ? merchCartItems.length > 0
    : cart.length > 0 && cart[0]?.total > 0;
  const isZeroTotalSellable =
    product?.type === EnumProductType.Fee ||
    product?.type === EnumProductType.RenewableFee ||
    product?.type === EnumProductType.Event;

  const isMainProductComplete = isCatalog
    ? merchCartItems.length > 0
    : isMerch
      ? isMerchComplete
      : cart.length > 0 && (cart[0].total > 0 || isZeroTotalSellable);
  const isAccommodationMainProductIsComplete =
    isMainProductComplete &&
    cart[0]?.productType === EnumProductType.Accommodation &&
    !!product
      ? cart[0].startDateISO.length > 0 &&
        cart[0].endDateISO.length > 0 &&
        getTotalGuestCount(cart[0].guestsPerTier) >=
          (product.accommodationMetadata?.guestMetadata?.minimumNumber ?? 0)
      : true;
  const isQuestionnaireRequired = !!booker?.survey?.questions.some(
    q => q.required && q.isActive,
  );
  const isQuestionnaireComplete = isQuestionnaireRequired
    ? questionnaireComplete
    : true;

  const isRenewableFee = product?.type === EnumProductType.RenewableFee;
  const isOfflineLikePayment =
    paymentMethodId === "bank_transfer" ||
    paymentMethodId === "offline_payment";
  const isEvent = product?.type === EnumProductType.Event;
  const guestInfoRequired =
    isEvent && (booker?.customerFields?.guestInfoRequired ?? false);
  const guestNamesEnabled =
    isEvent && (booker?.customerFields?.guestNamesEnabled ?? false);
  const guestEmailRequired =
    guestNamesEnabled && (booker?.customerFields?.guestEmailEnabled ?? false);
  const isEventGuestDetailsComplete = !guestInfoRequired ||
    eventGuestDetails.every(
      g =>
        g.name.trim() &&
        (!guestEmailRequired || g.email.trim()),
    );
  const hasChoices = sellingConfig.options.length > 0;
  const isTierRequired = hasChoices && !hasMerchVariants && !isEvent;
  const isEventPackageSelectionComplete = isEvent
    ? isEventSelectionComplete(cart, product, sellingConfig.options)
    : false;
  const isTierSelected = isEvent
    ? isEventPackageSelectionComplete
    : !isTierRequired || !!selectedTierId;

  const isTeamVisible = (booker as any)?.teamVisible === true;
  const { operators: orgScopedOperators, loading: orgScopedOperatorsLoading } =
    useOrgScopedUserOperators(
      userOperators,
      organizationId,
      isTeamVisible && !!organizationId,
    );
  const effectiveUserOperators =
    organizationId && isTeamVisible ? orgScopedOperators : userOperators ?? [];
  const showOperatorDropdown =
    isTeamVisible &&
    !orgScopedOperatorsLoading &&
    effectiveUserOperators.length > 1;
  const isOperatorSelected = showOperatorDropdown
    ? !!selectedOperatorId
    : true;

  const isDepositEnabled = booker?.paymentMetadata?.depositSettings?.enabled;
  const totalDue =
    paymentRequestGrandTotal != null
      ? paymentRequestGrandTotal
      : !isRenewableFee && isDepositEnabled
        ? depositTotal
        : grandTotal;
  const isPadOverLimit =
    !isBookingRequest &&
    !isInvoicedAfterService &&
    (booker?.paymentMetadata?.stripeRegion ?? "CA") === "CA" &&
    paymentMethodId === "direct_debit" &&
    totalDue > 300000;

  // A claimed tax exemption must carry a valid exemption number before the
  // order can be submitted (effectiveTaxExempt is only true once it does).
  // Skipped when the exemption UI isn't reachable — $0 orders hide the
  // payment-method step, and a booker without offerTaxExemption never shows
  // the toggle — so a stale snapshot flag can't dead-end the checkout.
  const offerTaxExemption = !!(booker?.paymentMetadata as any)
    ?.offerTaxExemption;
  const isTaxExemptionComplete =
    !taxExempt || effectiveTaxExempt || totalDue === 0 || !offerTaxExemption;

  const needsPaymentMethod = isRenewableFee
    ? totalDue > 0
    : totalDue > 0 && !isBookingRequest && !isInvoicedAfterService;
  const hasPaymentMethod =
    !!stripeCardElement ||
    paymentMethodId === "direct_debit" ||
    isOfflineLikePayment;

  const selectionLabel = isEvent
    ? t("CHECKOUT.CHECKLIST.ITEMS.SELECTION_TICKETS")
    : isMerch || isCatalog
      ? t("CHECKOUT.CHECKLIST.ITEMS.SELECTION_ITEMS")
      : getProductTypeConfig(product?.type).hasDates
        ? t("CHECKOUT.CHECKLIST.ITEMS.SELECTION_DATES")
        : t("CHECKOUT.CHECKLIST.ITEMS.SELECTION");

  const blockedMessage =
    getMultilingualString(
      customerEligibility.blockedMessage ?? {},
      language,
    ).trim() || t("CUSTOMER_DETAILS.NOT_ELIGIBLE");

  const earlyRenewalBlock = earlyRenewals.blocking[0];

  const requirement = (
    r: Omit<Requirement, "status"> & { status?: CheckoutRequirementStatus },
  ): Requirement => ({
    ...r,
    status: r.status ?? (r.met ? "complete" : "incomplete"),
  });

  const sharedTail: Requirement[] = [
    requirement({
      key: "paymentMethod",
      label: t("CHECKOUT.CHECKLIST.ITEMS.PAYMENT_METHOD"),
      met: !needsPaymentMethod || hasPaymentMethod,
      visible: needsPaymentMethod,
      section: "PAYMENT",
      stepIds: ["payment-method"],
    }),
    requirement({
      key: "taxExemption",
      label: t("CHECKOUT.CHECKLIST.ITEMS.TAX_EXEMPTION"),
      met: isTaxExemptionComplete,
      visible: !isTaxExemptionComplete,
      status: isTaxExemptionComplete ? "complete" : "error",
      section: "PAYMENT",
      stepIds: ["payment-method"],
    }),
    requirement({
      key: "padLimit",
      label: t("CHECKOUT.CHECKLIST.ITEMS.PAD_LIMIT"),
      met: !isPadOverLimit,
      visible: isPadOverLimit,
      status: isPadOverLimit ? "error" : "complete",
      section: "PAYMENT",
      stepIds: ["payment-method"],
    }),
    requirement({
      key: "policies",
      label: t("CHECKOUT.CHECKLIST.ITEMS.POLICIES"),
      met: acceptedPolicies,
      visible: true,
      section: "POLICIES",
      stepIds: ["policies"],
    }),
  ];

  const questionnaireRequirement = requirement({
    key: "questionnaire",
    label: t("CHECKOUT.CHECKLIST.ITEMS.QUESTIONNAIRE"),
    met: isQuestionnaireComplete,
    visible: isQuestionnaireRequired,
    section: BookerSection.Questionnaire,
    stepIds: ["questionnaire", "pricing-questions"],
  });

  const requirements: Requirement[] = isRenewableFee
    ? [
        requirement({
          key: "selection",
          label: selectionLabel,
          met: isTierSelected,
          visible: hasChoices,
          section: BookerSection.Product,
          stepIds: SELECTION_STEP_IDS,
        }),
        requirement({
          key: "priceQuote",
          label: t("CHECKOUT.CHECKLIST.ITEMS.PRICE_QUOTE"),
          met: !renewableFeeQuote.loading,
          visible: renewableFeeQuote.loading,
          status: renewableFeeQuote.loading ? "pending" : "complete",
          section: BookerSection.Product,
          stepIds: [],
        }),
        questionnaireRequirement,
        requirement({
          key: "yourDetails",
          label: t("CHECKOUT.CHECKLIST.ITEMS.YOUR_DETAILS"),
          met: customer.complete && !earlyRenewalBlock,
          visible: true,
          status: earlyRenewalBlock ? "error" : undefined,
          message: earlyRenewalBlock
            ? t(
                "CUSTOMER_DETAILS.RENEWAL_NOT_OPEN",
                getEarlyRenewalDates(earlyRenewalBlock, language),
              )
            : undefined,
          section: BookerSection.CustomerDetails,
          stepIds: ["customer-details"],
        }),
        requirement({
          key: "addOns",
          label: t("CHECKOUT.CHECKLIST.ITEMS.ADD_ONS"),
          met: isAddOnDetailsComplete,
          visible: !isAddOnDetailsComplete,
          section: BookerSection.AddOns,
          stepIds: ["add-ons"],
        }),
        ...sharedTail,
      ]
    : [
        requirement({
          key: "selection",
          label: selectionLabel,
          met:
            isMainProductComplete &&
            isAccommodationMainProductIsComplete &&
            isTierSelected,
          // Catalog items are picked in the store before checkout opens.
          visible: !isCatalog,
          section: BookerSection.Product,
          stepIds: SELECTION_STEP_IDS,
        }),
        requirement({
          key: "guestDetails",
          label: t("CHECKOUT.CHECKLIST.ITEMS.GUEST_DETAILS"),
          met: isEventGuestDetailsComplete,
          visible: guestInfoRequired,
          section: BookerSection.Product,
          stepIds: ["guest-details"],
        }),
        questionnaireRequirement,
        requirement({
          key: "yourDetails",
          label: t("CHECKOUT.CHECKLIST.ITEMS.YOUR_DETAILS"),
          met:
            customer.complete &&
            !customerEligibility.loading &&
            !customerEligibility.blocked,
          visible: true,
          status: customerEligibility.blocked
            ? "error"
            : customer.complete && customerEligibility.loading
              ? "pending"
              : undefined,
          message: customerEligibility.blocked ? blockedMessage : undefined,
          section: BookerSection.CustomerDetails,
          stepIds: ["customer-details"],
        }),
        requirement({
          key: "teamMember",
          label: t("CHECKOUT.CHECKLIST.ITEMS.TEAM_MEMBER"),
          met: isOperatorSelected,
          visible: showOperatorDropdown,
          section: BookerSection.Operator,
          stepIds: ["operator-select"],
        }),
        requirement({
          key: "addOns",
          label: t("CHECKOUT.CHECKLIST.ITEMS.ADD_ONS"),
          met: isAddOnDetailsComplete,
          // Add-ons are optional; only surface them when a picked one
          // still needs details.
          visible: !isAddOnDetailsComplete,
          section: BookerSection.AddOns,
          stepIds: ["add-ons"],
        }),
        requirement({
          key: "delivery",
          label: t("CHECKOUT.CHECKLIST.ITEMS.DELIVERY"),
          met: isDeliveryComplete,
          visible: isDeliveryRequired,
          section: BookerSection.Delivery,
          stepIds: ["delivery"],
        }),
        ...sharedTail,
      ];

  const sectionRank: Requirement["section"][] = [
    ...resolveSectionOrder(booker?.customizations?.sectionOrder),
    "PAYMENT",
    "POLICIES",
  ];
  const items: CheckoutRequirement[] = requirements
    .filter(r => r.visible)
    .sort(
      (a, b) => sectionRank.indexOf(a.section) - sectionRank.indexOf(b.section),
    )
    .map(({ key, label, status, stepIds, message }) => ({
      key,
      label,
      status,
      stepIds,
      message,
    }));

  return {
    isComplete: requirements.every(r => r.met),
    items,
    totalDue,
    isOfflineLikePayment,
    isBookingRequest,
    isInvoicedAfterService,
    isMonthlyStatement,
  };
};
